import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ujmsCivilTracks, ujmsCivilFeeQuote } from "../../../services/ujmsApi";
import { cls, Field, inputCls, selectCls } from "../../ui";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** One input per relief field defined by the track catalog (money / text / date / bool). */
export function ReliefFields({ track, relief, onChange, testPrefix = "relief" }) {
  if (!track) return null;
  const set = (k, v) => onChange({ ...(relief || {}), [k]: v });
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`${testPrefix}-fields`}>
      {track.relief.map((f) => {
        const v = relief?.[f.key] ?? (f.type === "bool" ? !!f.default : "");
        const id = `${testPrefix}-${f.key}`;
        if (f.type === "bool") return <label key={f.key} className="flex items-center gap-2 text-sm pt-5"><input type="checkbox" checked={!!v} onChange={(e) => set(f.key, e.target.checked)} data-testid={id} /> {f.label}</label>;
        return (
          <Field key={f.key} label={f.label} required={f.required}>
            <input type={f.type === "money" ? "number" : f.type === "date" ? "date" : "text"} step={f.type === "money" ? "0.01" : undefined} className={inputCls} value={v} onChange={(e) => set(f.key, e.target.value)} data-testid={id} />
          </Field>
        );
      })}
    </div>
  );
}

/** Live filing-fee quote from the Clerk's tiered schedule. */
export function FeeQuote({ track, amount, juryDemand, caseType, possessionOnly, testId = "fee-quote" }) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    const params = { track: track || undefined, amount: amount === "" || amount == null ? undefined : Number(amount), jury_demand: !!juryDemand, case_type: caseType || undefined, possession_only: !!possessionOnly };
    const t = setTimeout(() => ujmsCivilFeeQuote(params).then((r) => setQ(r.data)).catch(() => setQ(null)), 250);
    return () => clearTimeout(t);
  }, [track, amount, juryDemand, caseType, possessionOnly]);
  if (!q) return null;
  return (
    <div className="border border-emerald-300 bg-emerald-50 rounded-sm px-3 py-2 text-sm" data-testid={testId}>
      <div className="flex items-center justify-between"><span className="font-semibold text-emerald-900">Filing fee (auto-calculated)</span><span className="font-heading font-bold text-[16px] text-emerald-900" data-testid={`${testId}-total`}>{money(q.fee)}</span></div>
      <ul className="text-xs text-emerald-900/80 mt-1">{q.breakdown.map((b, i) => <li key={i} className="flex justify-between"><span>{b.item}</span><span>{money(b.amount)}</span></li>)}</ul>
      <div className="text-[10.5px] text-emerald-900/60 mt-1 italic">{q.authority}</div>
    </div>
  );
}

/** Civil lawsuit track selector — sets case type/subtype, exposes the relief schema and the live fee quote. */
export function CivilTrackPicker({ form, setForm }) {
  const [cat, setCat] = useState(null);
  useEffect(() => { ujmsCivilTracks().then((r) => setCat(r.data)).catch(() => toast.error("Civil track catalog unavailable")); }, []);
  if (!cat) return null;
  const track = cat.tracks.find((t) => t.key === form.civil_track);
  const choose = (key) => {
    const t = cat.tracks.find((x) => x.key === key);
    if (!t) { setForm((f) => ({ ...f, civil_track: "", relief: {} })); return; }
    const amt = Number(form.amount_in_controversy || 0);
    const type = t.law_type && amt > (cat.fees.law_division_threshold || 30000) ? t.law_type : t.case_type;
    setForm((f) => ({ ...f, civil_track: key, case_type: type, case_subtype: t.case_subtype, relief: { ...(f.relief || {}) }, auto_route: true, _track: t,
      initiating_filing: f.initiating_filing || { filing_type: t.filing_type, filing_title: `${t.filing_type} — ${t.label}`, document: null } }));
  };
  const amount = form.relief?.amount ?? form.amount_in_controversy;
  return (
    <div className="border-t border-slate-200 p-4 space-y-3" data-testid="civil-track-picker">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Civil lawsuit track">
          <select className={cls(selectCls, "min-w-[320px]")} value={form.civil_track || ""} onChange={(e) => choose(e.target.value)} data-testid="intake-civil-track">
            <option value="">— general civil (no track) —</option>
            {cat.tracks.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        {track && <div className="text-xs text-slate-600 max-w-xl"><b>{track.label}</b> · {track.statute} · parties: {track.roles.filter(Boolean).join(" / ")}{track.summons ? ` · ${track.summons.label || "Summons"} to ${track.summons.method === "SHERIFF" ? "Sheriff" : track.summons.method}` : track.publication ? " · notice by publication (no summons)" : ""}</div>}
      </div>
      {track && <ReliefFields track={track} relief={form.relief} onChange={(r) => setForm((f) => ({ ...f, relief: r, amount_in_controversy: r.amount ?? f.amount_in_controversy }))} />}
      {(track || ["CIVIL", "LAW"].includes(form.case_type)) && <FeeQuote track={form.civil_track} amount={amount} juryDemand={form.jury_demand} caseType={form.case_type} possessionOnly={form.civil_track === "EVICTION" && !(Number(form.relief?.amount || 0) > 0)} testId="intake-fee-quote" />}
    </div>
  );
}
